import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  listSensitiveData,
  deleteSensitiveData,
  updateSensitiveData,
  type SensitiveDataEntry,
} from '../../api/services/knowledge';
import { useWorkspace } from '../Workspace/artifacts';

const dataTypeLabels: Record<string, string> = {
  email: 'Email',
  iban: 'IBAN',
  phone: 'Phone',
  name: 'Name',
  address: 'Address',
  credit_card: 'Credit Card',
  tax_id: 'Tax ID',
  postal_code: 'Postal Code',
  other: 'Other',
};

const dataTypeOptions = Object.entries(dataTypeLabels).map(([value, label]) => ({
  value,
  label,
}));

interface EditResult {
  success: boolean;
  affectedDocs: string[];
}

interface EditModalProps {
  entry: SensitiveDataEntry;
  onClose: () => void;
  onSave: (
    code: string,
    updates: { original_value?: string; data_type?: string },
  ) => Promise<EditResult>;
}

const EditModal: React.FC<EditModalProps> = ({ entry, onClose, onSave }) => {
  const [originalValue, setOriginalValue] = React.useState(entry.original_value);
  const [dataType, setDataType] = React.useState(entry.data_type);
  const [isSaving, setIsSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [result, setResult] = React.useState<EditResult | null>(null);

  const hasChanges = originalValue !== entry.original_value || dataType !== entry.data_type;

  const handleSave = async () => {
    if (!hasChanges) return;

    setIsSaving(true);
    setError(null);

    try {
      const updates: { original_value?: string; data_type?: string } = {};
      if (originalValue !== entry.original_value) {
        updates.original_value = originalValue;
      }
      if (dataType !== entry.data_type) {
        updates.data_type = dataType;
      }
      const saveResult = await onSave(entry.code, updates);
      setResult(saveResult);
      // Auto-close if no affected docs
      if (saveResult.affectedDocs.length === 0) {
        onClose();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  // Show success result
  if (result) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100">
              <svg
                className="h-5 w-5 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-neutral-900">Updated Successfully</h3>
              <p className="text-sm text-neutral-500">The entry has been modified.</p>
            </div>
          </div>

          {result.affectedDocs.length > 0 && (
            <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
              <div className="font-medium mb-1">
                ✓ {result.affectedDocs.length} document(s) will show the new value:
              </div>
              <ul className="list-disc list-inside text-xs">
                {result.affectedDocs.slice(0, 5).map((doc, i) => (
                  <li key={i}>{doc}</li>
                ))}
                {result.affectedDocs.length > 5 && (
                  <li className="text-neutral-500">...and {result.affectedDocs.length - 5} more</li>
                )}
              </ul>
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg bg-neutral-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-neutral-800"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-neutral-900">Edit Sensitive Data</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-700"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-neutral-700">Code</label>
            <div className="rounded-lg bg-neutral-100 px-3 py-2 font-mono text-sm text-neutral-600">
              {entry.code}
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-neutral-700">Data Type</label>
            <select
              value={dataType}
              onChange={(e) => setDataType(e.target.value)}
              className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            >
              {dataTypeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-neutral-700">
              Original Value
            </label>
            <input
              type="text"
              value={originalValue}
              onChange={(e) => setOriginalValue(e.target.value)}
              className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              placeholder="Enter the original value"
            />
          </div>

          {entry.documents && entry.documents.length > 0 && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
              📄 Changes will apply to {entry.documents.length} document(s).
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 transition hover:bg-neutral-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

interface DeleteResult {
  success: boolean;
  updatedDocs: string[];
}

interface DeleteConfirmProps {
  entry: SensitiveDataEntry;
  onClose: () => void;
  onConfirm: (code: string) => Promise<DeleteResult>;
}

const DeleteConfirm: React.FC<DeleteConfirmProps> = ({ entry, onClose, onConfirm }) => {
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [result, setResult] = React.useState<DeleteResult | null>(null);

  const handleDelete = async () => {
    setIsDeleting(true);
    setError(null);
    try {
      const deleteResult = await onConfirm(entry.code);
      setResult(deleteResult);
      // Auto-close after showing success for 2 seconds if there are updated docs
      if (deleteResult.updatedDocs.length === 0) {
        onClose();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    } finally {
      setIsDeleting(false);
    }
  };

  // Show success result
  if (result) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-2xl">
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100">
              <svg
                className="h-5 w-5 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-neutral-900">Deleted Successfully</h3>
              <p className="text-sm text-neutral-500">The entry has been removed.</p>
            </div>
          </div>

          {result.updatedDocs.length > 0 && (
            <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
              <div className="font-medium mb-1">
                ✓ Updated {result.updatedDocs.length} document(s):
              </div>
              <ul className="list-disc list-inside text-xs">
                {result.updatedDocs.slice(0, 5).map((doc, i) => (
                  <li key={i}>{doc}</li>
                ))}
                {result.updatedDocs.length > 5 && (
                  <li className="text-neutral-500">...and {result.updatedDocs.length - 5} more</li>
                )}
              </ul>
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg bg-neutral-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-neutral-800"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
            <svg
              className="h-5 w-5 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">Delete Entry?</h3>
            <p className="text-sm text-neutral-500">
              This will restore the original value in documents.
            </p>
          </div>
        </div>

        <div className="mb-4 rounded-lg bg-neutral-50 p-3">
          <div className="text-xs font-medium uppercase text-neutral-500">
            {dataTypeLabels[entry.data_type] || entry.data_type}
          </div>
          <div className="mt-1 font-medium text-neutral-900">{entry.original_value}</div>
          <div className="mt-1 font-mono text-xs text-neutral-500">{entry.code}</div>
        </div>

        {entry.documents && entry.documents.length > 0 && (
          <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
            📄 Will restore original value in {entry.documents.length} document(s).
          </div>
        )}

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 transition hover:bg-neutral-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={isDeleting}
            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
};

export const SensitiveDataTable: React.FC = () => {
  const navigate = useNavigate();
  const { openArtifact, refreshArtifacts } = useWorkspace();
  const [entries, setEntries] = React.useState<SensitiveDataEntry[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [editingEntry, setEditingEntry] = React.useState<SensitiveDataEntry | null>(null);
  const [deletingEntry, setDeletingEntry] = React.useState<SensitiveDataEntry | null>(null);
  const [searchFilter, setSearchFilter] = React.useState('');
  const [typeFilter, setTypeFilter] = React.useState<string>('all');

  const handleDocumentClick = (documentId: string) => {
    const normalizedId = documentId.replace(/^artifact:/, '').replace(/:[0-9]+$/, '');
    openArtifact(normalizedId);
    navigate(`/?artifact=${normalizedId}`);
  };

  const loadData = React.useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await listSensitiveData();
      setEntries(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sensitive data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void loadData();
  }, [loadData]);

  const handleDelete = async (code: string): Promise<DeleteResult> => {
    const response = await deleteSensitiveData(code);
    setEntries((prev) => prev.filter((e) => e.code !== code));
    // Refresh all artifacts to show restored original values
    await refreshArtifacts();
    return {
      success: response.success,
      updatedDocs: response.updated_documents || [],
    };
  };

  const handleUpdate = async (
    code: string,
    updates: { original_value?: string; data_type?: string },
  ): Promise<EditResult> => {
    const response = await updateSensitiveData(code, updates);
    setEntries((prev) =>
      prev.map((e) =>
        e.code === code
          ? {
              ...e,
              original_value: response.entry.original_value,
              data_type: response.entry.data_type,
            }
          : e,
      ),
    );
    // Refresh all artifacts to show updated values
    await refreshArtifacts();
    return {
      success: response.success,
      affectedDocs: response.affected_documents || [],
    };
  };

  // Filter entries
  const filteredEntries = React.useMemo(() => {
    return entries.filter((entry) => {
      const matchesSearch =
        !searchFilter ||
        entry.original_value.toLowerCase().includes(searchFilter.toLowerCase()) ||
        entry.code.toLowerCase().includes(searchFilter.toLowerCase());
      const matchesType = typeFilter === 'all' || entry.data_type === typeFilter;
      return matchesSearch && matchesType;
    });
  }, [entries, searchFilter, typeFilter]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-sm text-neutral-500">Loading sensitive data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        {error}
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-8 text-center text-sm text-neutral-500">
        No sensitive data found. Sensitive data will appear here once detected in your documents.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <svg
            className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            placeholder="Search by value or code..."
            className="w-full rounded-lg border border-neutral-300 py-2 pl-10 pr-4 text-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
          />
        </div>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="rounded-lg border border-neutral-300 px-3 py-2 text-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
        >
          <option value="all">All Types</option>
          {dataTypeOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <div className="text-sm text-neutral-500">
          {filteredEntries.length} of {entries.length} entries
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-neutral-200">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-neutral-200 bg-neutral-50">
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-700">
                Type
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-700">
                Original Value
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-700">
                Code
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-700">
                Documents
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-700">
                Also Found In
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-neutral-700">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200">
            {filteredEntries.map((entry) => (
              <tr key={entry.code} className="group transition-colors hover:bg-neutral-50">
                <td className="px-4 py-3 text-sm">
                  <span className="inline-flex items-center rounded-full border border-neutral-200 bg-neutral-50 px-2.5 py-0.5 text-xs font-semibold text-neutral-700">
                    {dataTypeLabels[entry.data_type] || entry.data_type}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm font-medium text-neutral-900">
                  <span
                    className="inline-block max-w-[200px] truncate"
                    title={entry.original_value}
                  >
                    {entry.original_value}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm font-mono text-neutral-600">{entry.code}</td>
                <td className="px-4 py-3 text-sm">
                  {entry.documents && entry.documents.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {entry.documents.slice(0, 3).map((doc) => (
                        <button
                          key={doc.id}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDocumentClick(doc.id);
                          }}
                          className="inline-flex items-center rounded-md border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 transition hover:border-blue-300 hover:bg-blue-100"
                          title={`Open ${doc.title}`}
                        >
                          {doc.title.length > 20 ? `${doc.title.slice(0, 20)}...` : doc.title}
                        </button>
                      ))}
                      {entry.documents.length > 3 && (
                        <span className="inline-flex items-center px-2 py-1 text-xs text-neutral-500">
                          +{entry.documents.length - 3} more
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-neutral-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm">
                  {entry.other_occurrences && entry.other_occurrences.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {entry.other_occurrences.slice(0, 2).map((doc) => (
                        <button
                          key={doc.id}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDocumentClick(doc.id);
                          }}
                          className="inline-flex items-center rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700 transition hover:border-amber-300 hover:bg-amber-100"
                          title={`Open ${doc.title}`}
                        >
                          {doc.title.length > 15 ? `${doc.title.slice(0, 15)}...` : doc.title}
                        </button>
                      ))}
                      {entry.other_occurrences.length > 2 && (
                        <span className="inline-flex items-center px-2 py-1 text-xs text-neutral-500">
                          +{entry.other_occurrences.length - 2} more
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-neutral-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      type="button"
                      onClick={() => setEditingEntry(entry)}
                      className="rounded-lg p-1.5 text-neutral-500 transition hover:bg-neutral-100 hover:text-blue-600"
                      title="Edit"
                    >
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeletingEntry(entry)}
                      className="rounded-lg p-1.5 text-neutral-500 transition hover:bg-red-50 hover:text-red-600"
                      title="Delete"
                    >
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit Modal */}
      {editingEntry && (
        <EditModal
          entry={editingEntry}
          onClose={() => setEditingEntry(null)}
          onSave={handleUpdate}
        />
      )}

      {/* Delete Confirmation */}
      {deletingEntry && (
        <DeleteConfirm
          entry={deletingEntry}
          onClose={() => setDeletingEntry(null)}
          onConfirm={handleDelete}
        />
      )}
    </div>
  );
};

export default SensitiveDataTable;
